/**
 * Detect whether a string is HTML email content vs plain text.
 *
 * Plain-text reply chains often include angle-bracket addresses like
 * `Name <user@domain.com>`. Those must not be treated as HTML tags —
 * otherwise the body is injected without pre-wrap and newlines collapse.
 */
const PERSISTED_EMAIL_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isPersistedEmailId(id) {
  return PERSISTED_EMAIL_ID_RE.test(String(id || "").trim());
}

function emailFromRecipientPart(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const angle = trimmed.match(/<([^>]+)>/);
    return (angle ? angle[1] : trimmed).trim();
  }
  return String(value.email || value.address || "").trim();
}

/**
 * Compose "To/Cc/Bcc" from either a structured address array (`toAddresses`)
 * or a preformatted fallback string (`to`). Draft list/GET payloads often
 * have only the array.
 */
export function emailsFromRecipientField(addresses, fallbackString) {
  const fromList = (value) => {
    if (!value) return [];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        try {
          return fromList(JSON.parse(trimmed));
        } catch (_) { /* treat as a comma-separated line */ }
      }
      return trimmed.split(",").map(emailFromRecipientPart).filter(Boolean);
    }
    if (Array.isArray(value)) {
      return value.map(emailFromRecipientPart).filter(Boolean);
    }
    const single = emailFromRecipientPart(value);
    return single ? [single] : [];
  };
  const fromAddresses = fromList(addresses);
  if (fromAddresses.length > 0) return fromAddresses;
  return fromList(fallbackString);
}

/** IMAP UID accidentally scoped as `accountId:25` by list React keys. */
function stripAccountScopedUid(id) {
  const raw = String(id || "").trim();
  const scoped = raw.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:(\d+)$/i);
  return scoped ? scoped[1] : raw;
}

/**
 * Id to send to classify / GET. Prefer a real EmailMessage UUID; never send
 * the All-Accounts React key (`accountId:uid`).
 */
export function classifyMessageId(email) {
  if (!email) return undefined;
  const candidates = [email.id, email._id, email.messageId, email.providerMessageId];
  const uuid = candidates.find((x) => isPersistedEmailId(x));
  if (uuid) return String(uuid);
  const raw = email.providerMessageId || email.messageId || email.id || email._id;
  if (raw == null || String(raw).trim() === "") return undefined;
  return stripAccountScopedUid(raw);
}

const LIVE_CLASSIFY_TEXT_MAX = 4000;

function clipText(value, max) {
  if (value == null || value === "") return undefined;
  const s = String(value);
  return s.length > max ? s.slice(0, max) : s;
}

const FIRST_EMAIL_SENTENCE_MAX = 180;

/** html-to-text / tracking pixels often prefix the body with image and link URLs. */
function cleanEmailPreviewSource(text) {
  let cleaned = String(text || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[[^\]]*\]\(https?:\/\/[^)]*\)/gi, " ")
    .replace(/<https?:\/\/[^>\s]+>/gi, " ")
    .replace(/https?:\/\/[^\s<>"'\]\)]+/gi, " ")
    .replace(/\bwww\.[^\s<>"'\]\)]+/gi, " ")
    .replace(/\bcid:[^\s<>"'\]\)]+/gi, " ")
    .replace(/\[\s*\]/g, " ")
    .replace(/\(\s*\)/g, " ")
    .replace(/\b[\w.-]+\.(?:png|jpe?g|gif|svg|webp|bmp|ico|pdf|docx?|xlsx?|pptx?|zip)\b/gi, " ");
  cleaned = cleaned.replace(/\s+/g, " ").trim();
  // Drop leftover image alt text such as "Jira-Logo" sitting in front of the prose.
  cleaned = cleaned.replace(/^(?:[\w.-]*logo|image\d+(?:\.\w+)?|spacer|pixel)\b[\s,:]*/i, "").trim();
  return cleaned;
}

/** First sentence of the email body, used as the default list preview. */
export function firstEmailSentence(text, max = FIRST_EMAIL_SENTENCE_MAX) {
  const cleaned = cleanEmailPreviewSource(text);
  if (!cleaned) return "";
  const match = cleaned.match(/^(.+?[.!?…]+)(?:\s|$)/);
  let sentence = match ? match[1].trim() : cleaned;
  if (sentence.length < 24 && cleaned.length > sentence.length) {
    const rest = cleaned.slice(sentence.length).trim();
    const next = rest.match(/^(.+?[.!?…]+)(?:\s|$)/);
    sentence = next ? `${sentence} ${next[1].trim()}` : cleaned;
  }
  if (sentence.length > max) return sentence.slice(0, max).trim();
  return sentence;
}

export function cleanChatMessageText(text) {
  return String(text || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function trimAtWord(text, max) {
  const value = String(text || "").trim();
  if (value.length <= max) return value;
  const cut = value.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  return (sp > 40 ? cut.slice(0, sp) : cut).trim();
}

const SUMMARY_FILLER_RE = /^(?:ich glaube(?:[,:]|\s+dass)?|ich denke(?:[,:]|\s+dass)?|meiner meinung nach|vielleicht(?: kann man da)?|dann würde man|dann würde|eigentlich|interessant(?:erweise)?|genau|also|naja|und|aber|ja|ok(?:ay)?|hmm+|well|so|i think(?: that)?|i believe(?: that)?|maybe|perhaps|actually)\s+/i;

function stripSummaryFiller(sentence) {
  let s = String(sentence || "").trim();
  for (let i = 0; i < 4; i++) {
    const next = s.replace(SUMMARY_FILLER_RE, "");
    if (next === s) break;
    s = next.trim();
  }
  return s;
}

function lcFirst(value) {
  const s = String(value || "");
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

function ensurePeriod(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  return /[.!?…]$/.test(s) ? s : `${s}.`;
}

function splitSummarySentences(cleaned) {
  return String(cleaned || "")
    .replace(/([.!?…]+)(\s+)/g, "$1\n")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * One-sentence gist of a chat/email body — paraphrases by dropping fillers
 * and compressing all points into a single sentence (never returns the raw original
 * when the source has more than one sentence).
 */
export function oneSentenceMessageSummary(text, max = 120) {
  const cleaned = cleanEmailPreviewSource(cleanChatMessageText(text));
  if (!cleaned) return "";

  const sentences = splitSummarySentences(cleaned);
  const parts = sentences
    .map((s) => stripSummaryFiller(s.replace(/[.!?…]+$/g, "").trim()))
    .filter((s) => s.length > 8);

  let gist;
  if (parts.length === 0) {
    gist = cleaned;
  } else if (parts.length === 1) {
    gist = parts[0];
  } else if (parts.length === 2) {
    gist = `${parts[0]}, ${lcFirst(parts[1])}`;
  } else {
    gist = `${parts[0]}, ${lcFirst(parts[1])}, ${lcFirst(parts[parts.length - 1])}`;
  }

  return ensurePeriod(trimAtWord(gist, max));
}

/**
 * Preview line for inbox / messages-list email rows.
 * Default: first sentence of the body. Opt-in: stored AI one-sentence summary.
 * Tracking pixels / image URLs at the start of html-to-text bodies are skipped.
 */
export function getEmailListPreview(email, summaryEnabled) {
  if (!email) return "";
  if (summaryEnabled) {
    const summary = firstEmailSentence(email.snippet);
    if (summary) return summary;
  }
  const sources = [
    email.bodyText,
    email.body,
    email.bodyPreview,
    email.snippet,
    email.preview,
  ];
  for (const source of sources) {
    const sentence = firstEmailSentence(source);
    if (sentence) return sentence;
  }
  return "";
}

// The room scan finishes within seconds of insert. A flag older than this
// belongs to a scan that died (server restart mid-sync), so the room chip /
// "Raum zuordnen" is shown again instead of staying hidden for good.
const CLASSIFICATION_PENDING_MAX_MS = 3 * 60 * 1000;

/** True while the inbound room scan for this mail is still running. */
export function isClassificationPending(email) {
  if (!email || !email.classificationPending) return false;
  const insertedAt = new Date(email.createdAt || 0).getTime();
  if (!insertedAt) return true;
  return Date.now() - insertedAt < CLASSIFICATION_PENDING_MAX_MS;
}

/**
 * Lightweight copy for the assign popup. Sent IMAP rows carry full HTML
 * bodies; putting those in modal state freezes the UI.
 */
export function assignModalSnapshot(email) {
  if (!email) return null;
  return {
    id: email.id,
    _id: email._id,
    messageId: email.messageId,
    providerMessageId: email.providerMessageId,
    internetMessageId: email.internetMessageId,
    subject: email.subject,
    snippet: clipText(email.snippet, 280),
    from: email.from,
    fromName: email.fromName,
    fromAddress: email.fromAddress,
    to: email.to,
    toAddresses: email.toAddresses,
    cc: email.cc,
    ccAddresses: email.ccAddresses,
    date: email.date,
    receivedAt: email.receivedAt,
    threadId: email.threadId,
    hasAttachments: email.hasAttachments,
    isStarred: email.isStarred,
    folder: email.folder,
    direction: email.direction,
    labels: email.labels,
    __accountId: email.__accountId || email.emailAccount || email.emailAccountId,
    emailAccount: email.emailAccount,
    emailAccountId: email.emailAccountId,
    linkedProjects: email.linkedProjects,
    bodyText: clipText(email.bodyText, LIVE_CLASSIFY_TEXT_MAX),
  };
}

export function getEmailAccountId(email) {
  if (!email) return null;
  const raw = email.__accountId || email.accountId || email.emailAccountId || email.emailAccount;
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") return raw.id || raw._id || null;
  return raw;
}

/** Use the email's mailbox only when it belongs to the current user. */
export function pickOwnedEmailAccountId(email, accounts, fallbackId) {
  const candidate = getEmailAccountId(email);
  if (candidate && Array.isArray(accounts)) {
    const owned = accounts.find((a) => String(a.id || a._id) === String(candidate));
    if (owned) return owned.id || owned._id;
  }
  return fallbackId;
}

/**
 * Account to use when opening compose for an existing message/draft.
 * Prefers the stored mailbox id, then the From address, then fallback.
 */
export function pickComposeAccountId(email, accounts, fallbackId) {
  const owned = pickOwnedEmailAccountId(email, accounts, null);
  if (owned) return owned;
  const fromEmail = String(
    email?.fromAddress?.email || email?.from || email?.__accountEmail || ""
  )
    .toLowerCase()
    .trim();
  if (fromEmail && Array.isArray(accounts)) {
    const byEmail = accounts.find(
      (a) => String(a.email || "").toLowerCase() === fromEmail
    );
    if (byEmail) return byEmail.id || byEmail._id;
  }
  return fallbackId;
}

export function looksLikeHtml(text) {
  if (!text || typeof text !== "string") return false;
  // Strip angle-bracket email addresses before testing for real tags
  const withoutEmails = text.replace(/<[^\s<>]+@[^\s<>]+>/g, "");
  // Plausible HTML tag: <tag>, </tag>, <tag ...>, <tag/>
  return /<\/?[a-z][a-z0-9:-]*(?:\s[^>]*)?\/?>/i.test(withoutEmails);
}

/** Stub persisted without real HTML: entities, no tags, almost no line breaks. */
export function isCollapsedEmailStub(value) {
  const s = String(value || "");
  if (!s.trim()) return false;
  const hasRealHtml = /<(?:div|p|br|table|span|html|body|img|blockquote)\b/i.test(s);
  if (hasRealHtml) return false;
  const hasEntities = /&(?:nbsp|amp|gt|lt|quot|#\d+|#x[0-9a-f]+);/i.test(s);
  const newlineCount = (s.match(/\n/g) || []).length;
  if (hasEntities && newlineCount < 3) return true;
  if (newlineCount === 0 && s.length > 120) return true;
  return looksMashedRun(s);
}

function visibleEmailText(html) {
  return String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote)>/gi, "\n")
    .replace(/<[^>]+>/g, "");
}

function htmlHasMashedText(html) {
  return visibleEmailText(html)
    .split(/\n+/)
    .some((block) => looksMashedRun(block));
}

function looksMashedRun(s) {
  const raw = String(s || "");
  if ((raw.match(/\n/g) || []).length >= 2) return false;
  const t = raw.replace(/\s+/g, " ").trim();
  if (t.length < 80) return false;
  if (/,[A-ZÄÖÜ]/.test(t)) return true;
  if (/\d[A-ZÄÖÜ]/.test(t) && /Hallo |Benutzername|Passwort|Portal/i.test(t)) return true;
  if (/\?(?:Beste|Best |Mit freundlichen)/i.test(t)) return true;
  if (/(GmbH)[A-ZÄÖÜ]/.test(t)) return true;
  if (/(?:From|Von|Sent|Gesendet|To|An|Subject|Betreff):/i.test(t) && /(?:From|Von|Sent|To|Subject):/i.test(t.replace(/^(From|Von|Sent|To|Subject):/i, ""))) {
    return true;
  }
  return false;
}

function decodeEmailEntities(text) {
  let s = String(text || "");
  for (let i = 0; i < 3; i++) {
    const next = s
      .replace(/&nbsp;/gi, " ")
      .replace(/&#160;/g, " ")
      .replace(/&#x0*a0;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#(\d+);/g, (_, n) => {
        const code = Number(n);
        return Number.isFinite(code) ? String.fromCharCode(code) : _;
      })
      .replace(/&#x([0-9a-f]+);/gi, (_, h) => {
        const code = parseInt(h, 16);
        return Number.isFinite(code) ? String.fromCharCode(code) : _;
      });
    if (next === s) break;
    s = next;
  }
  return s;
}

function escapeHtmlText(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Restore paragraphs/headers in a collapsed run that still has other formatted lines. */
export function insertMashedBreaks(text) {
  let s = decodeEmailEntities(text);
  if (!s) return "";
  s = s
    .replace(/(Hallo [^,]{1,80},)(?=\S)/g, "$1\n\n")
    .replace(/,(?=[A-ZÄÖÜ])/g, ", ")
    .replace(/(\d)(?=[A-ZÄÖÜ])/g, "$1\n\n")
    .replace(/\?(?=[A-ZÄÖÜ])/g, "?\n\n")
    .replace(/(GmbH)(?=[A-ZÄÖÜ])/g, "$1\n")
    .replace(/:(?=[A-ZÄÖÜ])/g, ": ")
    .replace(/(?<!\n)(From:|Von:|Sent:|Gesendet:|To:|An:|Cc:|Subject:|Betreff:)/g, "\n$1")
    .replace(/:\s*(https?:\/\/)/gi, ":\n$1")
    .replace(/\s+(https?:\/\/)/g, "\n$1")
    .replace(/(mailto:)/gi, "\n$1")
    .replace(/([.!?])\s*(Beste Gr(?:ü|ue)?(?:ß|ss)e|Best regards|Freundliche Gr(?:ü|ue)?(?:ß|ss)e|Mit freundlichen Gr(?:ü|ue)?(?:ß|ss)en)/gi, "$1\n\n$2")
    .replace(/\s+(Beste Gr(?:ü|ue)?(?:ß|ss)e|Best regards|Freundliche Gr(?:ü|ue)?(?:ß|ss)e|Mit freundlichen Gr(?:ü|ue)?(?:ß|ss)en)/gi, "\n\n$1")
    .replace(/(Beste Gr(?:ü|ue)?(?:ß|ss)e|Best regards)(?=\s*B\+L|[A-ZÄÖÜ])/gi, "$1\n\n")
    .replace(/(Password Reset)\s*(Delivery has failed)/i, "$1\n\n$2")
    .replace(/\s+(Remote Server returned)/gi, "\n$1")
    .replace(/\s*\|\s*/g, "\n")
    .replace(/\s+(Handelsregister:|Sitz der Gesellschaft:|Geschäftsführer:|Marktdaten)/g, "\n$1");
  return s
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/ {2,}/g, " ")
    .trim();
}

/** Insert <br> into mashed text nodes without stripping the rest of the HTML. */
export function repairMashedEmailHtml(html) {
  let s = String(html || "");
  if (!s || !htmlHasMashedText(s)) return s;
  const placeholders = [];
  s = s.replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, (m) => {
    placeholders.push(m);
    return `\u0000${placeholders.length - 1}\u0000`;
  });
  const repairCaptured = (text) => {
    if (!looksMashedRun(text)) return text;
    const repaired = insertMashedBreaks(text);
    if (repaired === decodeEmailEntities(text).trim()) return text;
    return escapeHtmlText(repaired)
      .replace(/\n\n+/g, "<br><br>")
      .replace(/\n/g, "<br>");
  };
  s = s.replace(/>([^<]+)</g, (all, text) => `>${repairCaptured(text)}<`);
  s = s.replace(/^\s*([^<]+)/, (all, text) => (all.startsWith("<") ? all : repairCaptured(text)));
  s = s.replace(/\u0000(\d+)\u0000/g, (_, i) => placeholders[Number(i)] || "");
  return s;
}

/** Decode common entities and restore line breaks in collapsed text/plain stubs. */
export function formatEmailPlainBody(text) {
  let s = decodeEmailEntities(text);
  if (!s) return "";
  s = s
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<p\b[^>]*>/gi, "")
    .replace(/<\/div>/gi, "\n")
    .replace(/<div\b[^>]*>/gi, "");
  s = s.replace(/<[^>]+>/g, " ");
  s = s
    .split("\n")
    .map((line) => (looksMashedRun(line) ? insertMashedBreaks(line) : line))
    .join("\n");
  return s
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/ {2,}/g, " ")
    .trim();
}

const BARE_URL_RE = /https?:\/\/[^\s<>"']+|www\.[^\s<>"']+/gi;

function splitUrlAndTrailing(raw) {
  const match = String(raw || "").match(/^(.*?)([.,;:!?]+)$/);
  if (match && match[1].length > 7) {
    return { url: match[1], trailing: match[2] };
  }
  return { url: raw, trailing: "" };
}

function hrefForUrl(url) {
  const trimmed = String(url || "").trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isUsableUrl(url) {
  const value = String(url || "");
  if (value.length < 8) return false;
  return /[.:]/.test(value.slice(value.indexOf("://") >= 0 ? value.indexOf("://") + 3 : 0));
}

function createEmailLinkEl(url) {
  const a = document.createElement("a");
  a.href = hrefForUrl(url);
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.textContent = url;
  return a;
}

function textNodeHasBareUrl(node) {
  const parent = node.parentElement;
  if (!parent) return false;
  if (parent.closest("a, script, style, textarea, code")) return false;
  BARE_URL_RE.lastIndex = 0;
  return BARE_URL_RE.test(node.nodeValue || "");
}

function getCaretCharOffset(root, node, offset) {
  try {
    if (!root || !node || !root.contains(node)) return null;
    const range = document.createRange();
    range.selectNodeContents(root);
    range.setEnd(node, offset);
    return range.toString().length;
  } catch (e) {
    return null;
  }
}

function setCaretCharOffset(root, offset) {
  if (!root || offset == null || offset < 0) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node = walker.nextNode();
  while (node) {
    const len = node.nodeValue.length;
    if (remaining <= len) {
      const range = document.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    remaining -= len;
    node = walker.nextNode();
  }
  const range = document.createRange();
  range.selectNodeContents(root);
  range.collapse(false);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

function rangeStillInRoot(root, range) {
  if (!root || !range) return false;
  try {
    return root.contains(range.startContainer) && root.contains(range.endContainer);
  } catch (e) {
    return false;
  }
}

function restoreSelection(root, saved) {
  if (!root || !saved) return;
  const sel = window.getSelection();
  if (!sel) return;
  if (rangeStillInRoot(root, saved.range)) {
    try {
      sel.removeAllRanges();
      sel.addRange(saved.range);
      return;
    } catch (e) {
      /* fall through to character-offset restore */
    }
  }
  if (saved.start == null) return;
  if (saved.end != null && saved.end !== saved.start) {
    setCaretCharOffset(root, saved.start);
    if (sel.rangeCount) {
      const startRange = sel.getRangeAt(0).cloneRange();
      setCaretCharOffset(root, saved.end);
      if (sel.rangeCount) {
        const range = sel.getRangeAt(0);
        range.setStart(startRange.startContainer, startRange.startOffset);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    }
    return;
  }
  setCaretCharOffset(root, saved.start);
}

/** Wrap bare http(s)/www URLs in plain text with <a> tags (HTML-escaped). */
export function linkifyPlainText(text) {
  if (!text) return "";
  const escaped = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(BARE_URL_RE, (raw) => {
    const { url, trailing } = splitUrlAndTrailing(raw);
    if (!isUsableUrl(url)) return raw;
    const href = hrefForUrl(url).replace(/&/g, "&amp;");
    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>${trailing}`;
  });
}

/** Wrap bare URLs in a contentEditable / HTML tree without touching existing anchors. */
export function linkifyElement(root) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return textNodeHasBareUrl(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  // Leave the caret alone when there is nothing to wrap. Character-offset
  // restore ignores empty lines (<br>/<div><br></div>), so Enter would jump
  // the cursor back to the previous line.
  if (nodes.length === 0) return;

  const sel = window.getSelection();
  const saved =
    sel && sel.rangeCount && root.contains(sel.anchorNode)
      ? {
          range: sel.getRangeAt(0).cloneRange(),
          start: getCaretCharOffset(root, sel.anchorNode, sel.anchorOffset),
          end: getCaretCharOffset(root, sel.focusNode, sel.focusOffset),
        }
      : null;

  let didReplace = false;
  nodes.forEach((textNode) => {
    const text = textNode.nodeValue || "";
    const re = new RegExp(BARE_URL_RE.source, "gi");
    const frag = document.createDocumentFragment();
    let last = 0;
    let match = re.exec(text);
    let replaced = false;
    while (match) {
      const { url, trailing } = splitUrlAndTrailing(match[0]);
      if (match.index > last) {
        frag.appendChild(document.createTextNode(text.slice(last, match.index)));
      }
      if (isUsableUrl(url)) {
        frag.appendChild(createEmailLinkEl(url));
        if (trailing) frag.appendChild(document.createTextNode(trailing));
        replaced = true;
      } else {
        frag.appendChild(document.createTextNode(match[0]));
      }
      last = match.index + match[0].length;
      match = re.exec(text);
    }
    if (!replaced) return;
    if (last < text.length) {
      frag.appendChild(document.createTextNode(text.slice(last)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
    didReplace = true;
  });

  if (didReplace) restoreSelection(root, saved);
}

export function handleComposeLinkifyInput(editor, nativeEvent) {
  if (!editor || !nativeEvent) return;
  const type = nativeEvent.inputType;
  const data = nativeEvent.data;
  if (
    (type === "insertText" && data && /[\s]/.test(data)) ||
    type === "insertParagraph" ||
    type === "insertLineBreak" ||
    type === "insertFromPaste" ||
    type === "insertFromDrop"
  ) {
    linkifyElement(editor);
  }
}

export function handleComposeLinkClick(event, editor) {
  const a = event.target?.closest?.("a");
  if (!a || !editor?.contains(a)) return;
  event.preventDefault();
  if (event.ctrlKey || event.metaKey) {
    const href = a.getAttribute("href");
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  }
}

export function hydrateComposeEditorBody(editor, body) {
  if (!editor || body == null || body === "") return;
  const source = String(body);
  const isHtml = looksLikeHtml(source);
  editor.innerHTML = isHtml ? source : linkifyPlainText(source).replace(/\n/g, "<br>");
  linkifyElement(editor);
}

/** Prefer the longer of two email bodies (HTML or text). Empty stays empty. */
export function preferLongerText(a, b) {
  const as = a == null ? "" : String(a);
  const bs = b == null ? "" : String(b);
  return bs.length > as.length ? b : a;
}

function extractAddr(value) {
  if (!value) return "";
  if (typeof value === "object") {
    return extractAddr(value.email || value.address || "");
  }
  const s = String(value).trim();
  if (!s) return "";
  const angle = s.match(/<([^>]+)>/);
  return (angle ? angle[1] : s).trim().toLowerCase();
}

function recipientKey(addresses, fallback) {
  if (Array.isArray(addresses) && addresses.length > 0) {
    const parts = addresses.map(extractAddr).filter(Boolean).sort();
    if (parts.length) return parts.join(",");
  }
  return String(fallback || "")
    .split(",")
    .map((s) => extractAddr(s.trim()))
    .filter(Boolean)
    .sort()
    .join(",");
}

export function isOutgoingEmail(email, activeFolder) {
  if (!email) return false;
  if (String(activeFolder || "").toUpperCase() === "SENT") return true;
  if (String(email.folder || email.providerFolder || "").toUpperCase() === "SENT") return true;
  if (String(email.direction || "").toLowerCase() === "outgoing") return true;
  const labels = Array.isArray(email.labels) ? email.labels : [];
  return labels.some((l) => {
    const key = String(l).toUpperCase();
    return key === "SENT" || key === "\\SENT";
  });
}

/**
 * Resolve the address shown as Von/From.
 *
 * Sent list rows often have the real mailbox sender on `from`, while a
 * mismatched DB row (IMAP UID collision with Inbox) still has the original
 * correspondent on `fromAddress`. Prefer the mailbox sender in that case.
 */
export function resolveEmailSender(email, activeFolder) {
  if (!email) return { name: "", email: "" };
  const objName = email.fromAddress?.name || "";
  const objEmail = extractAddr(email.fromAddress);
  const flatName = email.fromName || "";
  const flatEmail = extractAddr(email.from);
  if (isOutgoingEmail(email, activeFolder) && flatEmail && objEmail && flatEmail !== objEmail) {
    return { name: flatName || objName, email: flatEmail };
  }
  return { name: objName || flatName, email: objEmail || flatEmail };
}

export function formatEmailSenderDisplay(email, activeFolder) {
  const { name, email: addr } = resolveEmailSender(email, activeFolder);
  if (name && addr && extractAddr(name) !== addr) return `${name} <${addr}>`;
  return name || addr || "";
}

/**
 * Overlay a GET /email/messages/:id payload onto the list/provider row.
 * Never let a different message's From/To/body replace the row we opened.
 */
export function mergeEnrichedEmail(prev, full) {
  if (!prev) return full || null;
  if (!full) return prev;
  const prevFrom = extractAddr(prev.fromAddress) || extractAddr(prev.from);
  const fullFrom = extractAddr(full.fromAddress) || extractAddr(full.from);
  const identityConflict = !!(prevFrom && fullFrom && prevFrom !== fullFrom);
  const recipientsConflict = (() => {
    const prevTo = recipientKey(prev.toAddresses, prev.to);
    const fullTo = recipientKey(full.toAddresses, full.to);
    return !!(prevTo && fullTo && prevTo !== fullTo);
  })();

  const merged = {
    ...full,
    ...prev,
    bodyHtml: preferLongerText(prev.bodyHtml, full.bodyHtml),
    bodyText: preferLongerText(prev.bodyText, full.bodyText),
    body: preferLongerText(prev.body, full.body),
    snippet: full.snippet ?? prev.snippet,
    bodyPreview: firstEmailSentence(full.bodyText || full.body || prev.bodyText || "")
      || prev.bodyPreview
      || full.bodyPreview,
    attachments: (full.attachments && full.attachments.length)
      ? full.attachments
      : (prev.attachments || []),
    hasAttachments: !!(full.hasAttachments || prev.hasAttachments
      || (full.attachments && full.attachments.length)),
    linkedProjects: (Array.isArray(full.linkedProjects) && full.linkedProjects.length)
      ? full.linkedProjects
      : prev.linkedProjects,
    __accountId: prev.__accountId || full.emailAccount || full.emailAccountId || prev.emailAccountId,
  };

  if (identityConflict || recipientsConflict) {
    const fromAddress = prev.fromAddress || (prev.from
      ? { email: extractAddr(prev.from) || prev.from, name: prev.fromName || null }
      : null);
    return {
      ...merged,
      id: prev.id,
      _id: prev._id,
      providerMessageId: prev.providerMessageId,
      internetMessageId: prev.internetMessageId || prev.providerMessageId,
      linkedProjects: prev.linkedProjects,
      attachments: prev.attachments || [],
      fromAddress,
      from: prev.from || (fromAddress && fromAddress.email) || prev.from,
      fromName: prev.fromName,
      toAddresses: prev.toAddresses,
      to: prev.to,
      ccAddresses: prev.ccAddresses,
      cc: prev.cc,
      bccAddresses: prev.bccAddresses,
      bcc: prev.bcc,
      subject: prev.subject || merged.subject,
      bodyHtml: prev.bodyHtml,
      bodyText: prev.bodyText,
      body: prev.body,
      snippet: prev.snippet,
      bodyPreview: prev.bodyPreview,
    };
  }

  if (!prev.fromAddress && (full.fromAddress || prev.from || merged.from)) {
    merged.fromAddress = full.fromAddress || {
      email: extractAddr(prev.from || merged.from) || prev.from || merged.from,
      name: prev.fromName || merged.fromName || null,
    };
  }
  return merged;
}

function normalizeSentId(id) {
  return String(id || "")
    .trim()
    .replace(/^<|>$/g, "")
    .toLowerCase();
}

function isBareImapUid(id) {
  return /^\d+$/.test(id);
}

function sentAccountScope(m) {
  return String(m && (m.__accountId || m.emailAccount || m.emailAccountId) || "");
}

/**
 * IMAP Sent (UID) and the local outgoing DB row (SMTP Message-ID) are often
 * the same send listed twice. Collapse them for the Sent folder UI.
 * IMAP UIDs are folder-local, so they are scoped per mailbox — otherwise
 * All Accounts drops Sent items that happen to share a UID.
 */
export function dedupeSentFolderMessages(messages) {
  if (!Array.isArray(messages) || messages.length < 2) return messages || [];
  const seenIds = new Set();
  const seenFp = new Set();
  const out = [];
  for (const m of messages) {
    if (!m) continue;
    const acc = sentAccountScope(m);
    const ids = [
      m.id,
      m._id,
      m.providerMessageId,
      m.internetMessageId,
      m.messageId,
    ]
      .map(normalizeSentId)
      .filter((id) => id && !id.startsWith("outgoing:"))
      .map((id) => (isBareImapUid(id) ? `${acc}:${id}` : id));
    const subject = String(m.subject || "").trim().toLowerCase();
    const from = String(m.from || m.fromAddress?.email || m.fromName || "")
      .trim()
      .toLowerCase();
    const to = String(m.to || "")
      .split(",")
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join(",");
    const t = new Date(m.date || m.receivedAt || 0).getTime();
    const minute = Number.isFinite(t) ? Math.floor(t / 60000) : 0;
    const fp = `${acc}|${subject}|${from}|${to}|${minute}`;
    const weakFp = !subject && !from && !to;
    const idHit = ids.some((id) => seenIds.has(id));
    if (idHit || (!weakFp && fp && seenFp.has(fp))) continue;
    ids.forEach((id) => seenIds.add(id));
    if (!weakFp && fp) seenFp.add(fp);
    out.push(m);
  }
  return out;
}
